'use client';

import { useMemo, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'vale:recentMatters';
const MAX_ENTRIES = 10;

interface RecentMatterEntry {
  id: number;
  viewedAt: number;
}

const EMPTY: RecentMatterEntry[] = [];

let cachedRaw: string | null = null;
let cachedEntries: RecentMatterEntry[] = EMPTY;

function readEntries(): RecentMatterEntry[] {
  if (typeof window === 'undefined') return EMPTY;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === cachedRaw) return cachedEntries;
  cachedRaw = raw;
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    cachedEntries = Array.isArray(parsed) ? parsed : EMPTY;
  } catch {
    cachedEntries = EMPTY;
  }
  return cachedEntries;
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
}

/**
 * Ids of matters this user has recently viewed or selected from search,
 * most-recent first — persisted in browser localStorage (per-device,
 * LF-4960). Backs the search modal's "Recent" section, which previously
 * showed the firm's most-recently-*updated* matters (anyone's edits) rather
 * than this user's own recent activity.
 */
export function useRecentMatterIds(limit: number): number[] {
  const entries = useSyncExternalStore(subscribe, readEntries, () => EMPTY);
  return useMemo(
    () => entries.slice(0, limit).map((entry) => entry.id),
    [entries, limit]
  );
}

/** Record a matter as viewed just now, moving it to the front of the list. */
export function recordRecentMatterView(matterId: number): void {
  if (typeof window === 'undefined') return;
  try {
    const rest = readEntries().filter((entry) => entry.id !== matterId);
    const next = [{ id: matterId, viewedAt: Date.now() }, ...rest].slice(
      0,
      MAX_ENTRIES
    );
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    // The native `storage` event only fires in other tabs — dispatch one
    // here so this tab's subscribers (useRecentMatterIds) re-read too.
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
  } catch {
    // localStorage unavailable (private mode, etc.) — no-op.
  }
}
